import React, { useEffect, useState, useMemo, useContext, useCallback } from "react";
import {
    Calendar,
    Filter,
    Grid,
    List,
    ChevronLeft,
    ChevronRight,
    Search,
    RefreshCw,
    Users,
    Clock,
    CheckCircle,
    AlertTriangle,
    Edit2,
    Trash2,
    Save,
    X,
    User,
    Phone,
    FileText
} from "lucide-react";
import API from "../../../services/interceptor";
import Loading from "../../Loading";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/AuthContext";
import Dropdown from "../Comman/Dropdown";
import ReasonDropdown from "../Comman/ReasonDropdown";

// Time slots dropdown options
const TIME_SLOTS = [
    "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
    "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
    "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM",
    "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM"
];

// Visit reasons dropdown options
const VISIT_REASONS = [
    "Regular Checkup",
    "Follow-up",
    "Consultation",
    "Emergency",
    "Vaccination",
    "Blood Test",
    "X-Ray",
    "Physical Therapy",
    "Prescription Refill",
    "Health Screening",
    "Other"
];

// Helper function for status colors
const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch (statusLower) {
        case 'completed': return 'bg-green-100 text-green-800 border-green-200';
        case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
        case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
        default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
};

// Helper function for date formatting
const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

// VisitCard component extracted outside - THIS IS THE FIX
const VisitCard = ({ 
    item, 
    isEditing, 
    isLoading, 
    pendingUpdates,
    reasonOptions,
    timeBlocks,
    handleInputChange,
    handleUpdate,
    handleDelete,
    setEditingId,
    setPendingUpdates
}) => {
    // Get current value for each field (either pending update or original value)
    const getCurrentValue = useCallback((field) => {
        return pendingUpdates[item._id]?.[field] !== undefined 
            ? pendingUpdates[item._id][field] 
            : item[field];
    }, [item, pendingUpdates]);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 overflow-hidden">
            <div className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                            {item.name?.charAt(0)?.toUpperCase() || 'P'}
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
                                <User size={16} className="text-gray-500" />
                                {item.name || 'N/A'}
                            </h3>
                            <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                                <Phone size={14} />
                                {item.mobile || 'N/A'}
                            </p>
                        </div>
                    </div>
                    {isEditing ? (
                        <select
                            value={getCurrentValue('status')}
                            onChange={(e) => handleInputChange(item._id, 'status', e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded-full text-xs font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="Scheduled">Scheduled</option>
                            <option value="Completed">Completed</option>
                        </select>
                    ) : (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(item.status)}`}>
                            {item.status || 'N/A'}
                        </span>
                    )}
                </div>

                <div className="space-y-3 border-t pt-4">
                    <div className="flex items-center text-sm">
                        <span className="text-gray-500 w-24 font-medium">OPD:</span>
                        <span className="text-gray-900 font-semibold">{item.opdNumber || 'N/A'}</span>
                    </div>

                    <div className="flex items-center text-sm">
                        <span className="text-gray-500 w-24 font-medium">Date:</span>
                        {isEditing ? (
                            <input
                                type="date"
                                value={getCurrentValue('date') ? new Date(getCurrentValue('date')).toISOString().split('T')[0] : ''}
                                onChange={(e) => handleInputChange(item._id, 'date', e.target.value)}
                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                        ) : (
                            <span className="text-gray-900">{formatDate(item.date)}</span>
                        )}
                    </div>

                    <div className="flex items-center text-sm">
                        <span className="text-gray-500 w-24 font-medium">Time:</span>
                        {isEditing ? (
                            <Dropdown
                                item={"Procedure"}
                                selectedOption={getCurrentValue('time')}
                                onChange={(e) => handleInputChange(item._id, 'time', e.target.value)}
                                listOptions={timeBlocks}
                            />
                        ) : (
                            <span className="text-gray-900">{item.time || 'N/A'}</span>
                        )}
                    </div>

                    <div className="flex items-start text-sm">
                        <span className="text-gray-500 w-24 font-medium flex-shrink-0">Visit For:</span>
                        {isEditing ? (
                            <Dropdown
                                item={"Procedure"}
                                selectedOption={getCurrentValue('reason')}
                                onChange={(e) => handleInputChange(item._id, 'reason', e.target.value)}
                                listOptions={reasonOptions}
                            />
                        ) : (
                            <span className="text-gray-900 flex-1">{item.reason || 'N/A'}</span>
                        )}
                    </div>

                    {item.notes && !isEditing && (
                        <div className="flex items-start text-sm bg-gray-50 p-3 rounded-lg">
                            <FileText size={16} className="text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600 text-xs">{item.notes}</span>
                        </div>
                    )}

                    {isEditing && (
                        <div className="flex items-start text-sm">
                            <span className="text-gray-500 w-24 font-medium flex-shrink-0">Notes:</span>
                            <textarea
                                value={getCurrentValue('notes') || ''}
                                onChange={(e) => handleInputChange(item._id, 'notes', e.target.value)}
                                rows={2}
                                placeholder="Add notes..."
                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                            />
                        </div>
                    )}
                </div>

                <div className="flex gap-2 pt-4 border-t">
                    {isEditing ? (
                        <>
                            <button
                                onClick={() => handleUpdate(item)}
                                disabled={isLoading}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                <Save size={16} />
                                {isLoading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                onClick={() => {
                                    setEditingId(null);
                                    setPendingUpdates(prev => {
                                        const { [item._id]: _, ...rest } = prev;
                                        return rest;
                                    });
                                }}
                                disabled={isLoading}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                <X size={16} />
                                Cancel
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => setEditingId(item._id)}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                <Edit2 size={16} />
                                Edit
                            </button>
                            <button
                                onClick={() => handleDelete(item)}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                            >
                                <Trash2 size={16} />
                                Delete
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const AllVisits = () => {
    const {
        visits,
        visitsLoading,
        visitsError,
        getAllVisits,
        setVisits,
        setVisitsError,
        reasonOptions, 
        timeBlocks
    } = useContext(AuthContext);

    const [pendingUpdates, setPendingUpdates] = useState({});
    const [loadingUpdates, setLoadingUpdates] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [dateFilter, setDateFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sortOption, setSortOption] = useState("date");
    const [viewType, setViewType] = useState("card");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);

    useEffect(() => {
        if (!visits || visits.length === 0) {
            getAllVisits();
        }
    }, [visits, getAllVisits]);

    useEffect(() => {
        setCurrentPage(1);
    }, [dateFilter, statusFilter, sortOption, searchTerm]);

    const handleInputChange = useCallback((id, field, value) => {
        setPendingUpdates((prev) => ({
            ...prev,
            [id]: { ...prev[id], [field]: value },
        }));
    }, []);

    const handleUpdate = useCallback(async (visit) => {
        const { _id: visitId, reportId } = visit;
        const updates = pendingUpdates[visitId];

        if (!updates) {
            toast.info("No changes to update.");
            return;
        }

        setLoadingUpdates((prev) => ({ ...prev, [visitId]: true }));
        try {
            await API.put(`/api/visits/${reportId}/visits/${visitId}`, updates);
            setVisits((prev) =>
                prev.map((v) => (v._id === visitId ? { ...v, ...updates } : v))
            );
            setPendingUpdates((prev) => {
                const { [visitId]: _, ...rest } = prev;
                return rest;
            });
            setEditingId(null);
            toast.success("Visit updated successfully!");
        } catch (error) {
            const message = error.response?.data?.message || "Failed to update visit";
            setVisitsError(message);
            toast.error(message);
        } finally {
            setLoadingUpdates((prev) => ({ ...prev, [visitId]: false }));
        }
    }, [pendingUpdates, setVisits, setVisitsError]);

    const handleDelete = useCallback(async (visit) => {
        if (!window.confirm("Are you sure you want to delete this visit?")) return;

        const { _id: visitId, reportId } = visit;
        try {
            await API.delete(`/api/visits/${reportId}/visits/${visitId}`);
            setVisits((prev) => prev.filter((v) => v._id !== visitId));
            toast.success("Visit deleted successfully!");
        } catch (error) {
            const message = error.response?.data?.message || "Failed to delete visit";
            setVisitsError(message);
            toast.error(message);
        }
    }, [setVisits, setVisitsError]);

    const handleRefresh = useCallback(() => {
        getAllVisits();
        toast.info("Visits refreshed!");
    }, [getAllVisits]);

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const clearFilters = useCallback(() => {
        setSearchTerm("");
        setDateFilter("all");
        setStatusFilter("all");
        setSortOption("date");
        setCurrentPage(1);
    }, []);

    const filteredVisits = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let filtered = (visits || []).filter((visit) => {
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const matchesSearch =
                    visit.name?.toLowerCase().includes(searchLower) ||
                    visit.mobile?.toLowerCase().includes(searchLower) ||
                    visit.reason?.toLowerCase().includes(searchLower) ||
                    visit.opdNumber?.toLowerCase().includes(searchLower) ||
                    visit.status?.toLowerCase().includes(searchLower);
                if (!matchesSearch) return false;
            }
            if (dateFilter !== "all") {
                const visitDate = new Date(visit.date);
                visitDate.setHours(0, 0, 0, 0);
                switch (dateFilter) {
                    case "today": if (visitDate.getTime() !== today.getTime()) return false; break;
                    case "upcoming": if (visitDate.getTime() <= today.getTime()) return false; break;
                    case "past": if (visitDate.getTime() >= today.getTime()) return false; break;
                    default: break;
                }
            }
            if (statusFilter !== "all") {
                if (visit.status?.toLowerCase() !== statusFilter.toLowerCase()) return false;
            }
            return true;
        });

        if (sortOption !== "none") {
            filtered.sort((a, b) => {
                switch (sortOption) {
                    case "name": return (a.name || "").localeCompare(b.name || "");
                    case "date": return new Date(b.date) - new Date(a.date);
                    case "status": return (a.status || "").localeCompare(b.status || "");
                    default: return 0;
                }
            });
        }
        return filtered;
    }, [visits, dateFilter, statusFilter, sortOption, searchTerm]);

    const statistics = useMemo(() => {
        if (!visits) return { total: 0, today: 0, completed: 0, pending: 0 };
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return {
            total: visits.length,
            today: visits.filter(v => new Date(v.date).setHours(0, 0, 0, 0) === today.getTime()).length,
            completed: visits.filter(v => v.status?.toLowerCase() === 'completed').length,
            pending: visits.filter(v => v.status?.toLowerCase() === 'pending').length,
        };
    }, [visits]);

    const totalPages = Math.ceil(filteredVisits.length / itemsPerPage);
    const paginatedVisits = filteredVisits.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    if (visitsLoading && (!visits || visits.length === 0)) {
        return (
            <div className="flex justify-center items-center py-20">
                <Loading />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
            <div className="mx-auto p-4 sm:p-6 space-y-6">
                <header className="text-center bg-white rounded-2xl shadow-sm p-6 border">
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Visit Management</h1>
                    <p className="text-gray-600 text-lg">Manage and track all patient visits efficiently.</p>
                </header>

                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-blue-100 text-sm font-medium">Total Visits</p>
                                <p className="text-3xl font-bold">{statistics.total}</p>
                            </div>
                            <Users className="w-8 h-8 text-blue-200" />
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-emerald-100 text-sm font-medium">Today's Visits</p>
                                <p className="text-3xl font-bold">{statistics.today}</p>
                            </div>
                            <Clock className="w-8 h-8 text-emerald-200" />
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-purple-100 text-sm font-medium">Completed</p>
                                <p className="text-3xl font-bold">{statistics.completed}</p>
                            </div>
                            <CheckCircle className="w-8 h-8 text-purple-200" />
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-amber-100 text-sm font-medium">Pending</p>
                                <p className="text-3xl font-bold">{statistics.pending}</p>
                            </div>
                            <AlertTriangle className="w-8 h-8 text-amber-200" />
                        </div>
                    </div>
                </section>

                {visitsError && (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center justify-between">
                        <p className="text-red-700 font-medium">{visitsError}</p>
                        <button onClick={handleRefresh} className="text-red-600 hover:text-red-800 font-medium text-sm px-4 py-2 rounded-lg hover:bg-red-100 transition-colors">
                            Try Again
                        </button>
                    </div>
                )}

                <section className="bg-white rounded-2xl shadow-sm border p-6">
                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="relative flex-1 w-full max-w-md">
                            <Search size={20} className="absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by patient, mobile, OPD..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setViewType(viewType === 'card' ? 'table' : 'card')}
                                className="p-3 text-gray-500 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                                title={viewType === 'card' ? 'Switch to table view' : 'Switch to card view'}
                            >
                                {viewType === 'card' ? <List size={20} /> : <Grid size={20} />}
                            </button>
                            <button
                                onClick={handleRefresh}
                                className="p-3 text-gray-500 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                                title="Refresh visits"
                            >
                                <RefreshCw size={20} />
                            </button>
                            <div className="text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-xl">
                                <span className="font-semibold text-gray-900">{filteredVisits.length}</span>
                                <span className="ml-1">visits found</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-white rounded-2xl shadow-sm border p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Date Filter</label>
                            <select
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Dates</option>
                                <option value="today">Today</option>
                                <option value="upcoming">Upcoming</option>
                                <option value="past">Past</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Statuses</option>
                                <option value="scheduled">Scheduled</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                            <select
                                value={sortOption}
                                onChange={(e) => setSortOption(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="date">Date (Recent First)</option>
                                <option value="name">Patient Name</option>
                                <option value="status">Status</option>
                            </select>
                        </div>
                    </div>
                </section>

                <main>
                    {filteredVisits.length > 0 ? (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {paginatedVisits.map((item) => (
                                    <VisitCard 
                                        key={item._id} 
                                        item={item}
                                        isEditing={editingId === item._id}
                                        isLoading={loadingUpdates[item._id]}
                                        pendingUpdates={pendingUpdates}
                                        reasonOptions={reasonOptions}
                                        timeBlocks={timeBlocks}
                                        handleInputChange={handleInputChange}
                                        handleUpdate={handleUpdate}
                                        handleDelete={handleDelete}
                                        setEditingId={setEditingId}
                                        setPendingUpdates={setPendingUpdates}
                                    />
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <section className="bg-white rounded-2xl shadow-sm border p-6 flex justify-between items-center">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft size={20} />
                                        Previous
                                    </button>
                                    <span className="text-sm text-gray-600">
                                        Page <span className="font-semibold text-gray-900">{currentPage}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                                    </span>
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                        <ChevronRight size={20} />
                                    </button>
                                </section>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-16 bg-white rounded-2xl border">
                            <Calendar size={64} className="mx-auto text-gray-400 mb-6" />
                            <h3 className="text-2xl font-semibold text-gray-700 mb-3">No visits match your criteria</h3>
                            <p className="text-gray-500 mb-6 max-w-md mx-auto">Try adjusting your search or filters to find what you're looking for.</p>
                            <button
                                onClick={clearFilters}
                                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
                            >
                                Clear All Filters
                            </button>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default AllVisits;